import {
    isEmpty,
    isDingTalkUA,
    isLarkUA,
    isWxWorkUA,
    isWeChatUA,
    inIframe,
    isJodooAppUA,
    isJdyAppUA
} from '@libs/shared';
import { JodooSdk, JdySdk, DingTalkSdk, LarkSdk, WeChatSdk, WxWorkSdk } from '../integration-env';
import JsSdkBase from './js-sdk-base';
import { getValidAbsoluteUrl, sdkLogger } from '../utils';
import { sdkLog } from '../decorator';
import {
    FunctionKeys,
    SdkError,
    SDKFunction,
    SdkType,
    ShareLinkOptions,
    ShareLinkResult,
    type HeaderActionClickEvent
} from '../interface';
import { SdkResponseCode } from '../constant';

/**
 * jssdk 工具类
 * 用于向业务侧暴露jssdk API
 */
export default class H5JsApi {
    /**
     * 用于定位缓存的全局变量
     * @param timestamp 某次定位时的时间戳
     * @param location 某次定位时的坐标结果（注意这个结果是异步的）
     */
    private locationCache: {
        timestamp?: number;
        location?: ReturnType<JsSdkBase['getLocation']>;
    } = {};
    /**
     * 保存各环境下的 sdk 实例
     */
    private sdkAdapter: JsSdkBase;

    constructor(options?: {
        allowMap?: {
            // 当前环境是否可以调用 钉钉 jssdk
            [SdkType.DingTalk]?: boolean;
            // 当前环境是否可以调用 企微 jssdk
            [SdkType.WxWork]?: boolean;
            // 当前环境是否可以调用 飞书 jssdk
            [SdkType.Lark]?: boolean;
            // 当前环境是否可以调用 微信 jssdk
            [SdkType.WeChat]?: boolean;
            // 当前环境是否可以调用 jodoo app jssdk
            [SdkType.Jodoo]?: boolean;
            // 当前环境是否可以调用 jdy app jssdk
            [SdkType.Jdy]?: boolean;
        };
    }) {
        // 默认情况下，所有 jssdk 都认为是可用的，如果后面其他业务方有自己的个性化需求，则按需传入指定的参数即可
        const defaultAllowMap = {
            [SdkType.DingTalk]: true,
            [SdkType.WxWork]: true,
            [SdkType.Lark]: true,
            [SdkType.WeChat]: true,
            [SdkType.Jodoo]: true,
            [SdkType.Jdy]: true
        };
        const allowMap = {
            ...defaultAllowMap,
            ...(options?.allowMap || {})
        };
        this.sdkAdapter = (function getSdkAdapter() {
            /**
             * iframe嵌套场景不加载sdk
             * @see https://project.feishu.cn/b2rl2h/assignment/detail/6125871209
             * @see FXD-46808
             */
            if (inIframe()) {
                sdkLogger.warn('jssdk is blocked in iframe');
                return new JsSdkBase();
            }
            if (isDingTalkUA() && allowMap[SdkType.DingTalk]) {
                return new DingTalkSdk();
            }
            if (isLarkUA() && allowMap[SdkType.Lark]) {
                return new LarkSdk();
            }
            if (isWxWorkUA() && allowMap[SdkType.WxWork]) {
                return new WxWorkSdk();
            }
            if (isWeChatUA() && allowMap[SdkType.WeChat]) {
                return new WeChatSdk();
            }
            if (isJodooAppUA() && allowMap[SdkType.Jodoo]) {
                return new JodooSdk();
            }
            if (isJdyAppUA() && allowMap[SdkType.Jdy]) {
                return new JdySdk();
            }
            return new JsSdkBase();
        })();
    }

    /**
     * 初始化sdk
     * @param config 初始化参数
     */
    @sdkLog('h5-jsapi')
    init(config: Parameters<JsSdkBase['init']>[0]) {
        return this.sdkAdapter.init(config).finally(() => {
            /**
             * 这里包含了以下流程终止的场景：
             * 1. jssdk 鉴权完成（无论是成功还是失败），这是最完整的初始化流程
             * 2. 当前客户端环境是普通浏览器环境时，提前终止初始化流程
             * 3. jssdk 鉴权参数为空时，提前终止初始化流程
             * 4. jssdk 脚本加载失败，提前终止初始化流程
             */
            this.sdkAdapter.setIsSdkInitCompleted(true);
        });
    }

    initWWOpenData(config: Parameters<JsSdkBase['init']>[0]) {
        return new WxWorkSdk().init(config);
    }

    ready(callback: Parameters<JsSdkBase['ready']>[0]) {
        return this.sdkAdapter.ready(callback);
    }

    loaded(callback: Parameters<JsSdkBase['loaded']>[0]) {
        return this.sdkAdapter.loaded(callback);
    }

    error(callback: Parameters<JsSdkBase['error']>[0]) {
        return this.sdkAdapter.error(callback);
    }

    completed(callback: Parameters<JsSdkBase['completed']>[0]) {
        return this.sdkAdapter.completed(() => {
            sdkLogger.info(`${this.sdkType} jssdk init completed`);
            callback();
        });
    }

    /**
     * 用于需要手动临场获取jssdk鉴权参数并进行重新鉴权的场景
     */
    loadSdkConfig() {
        return this.sdkAdapter.loadSdkConfig();
    }

    asyncLoadScript() {
        return this.sdkAdapter.asyncLoadScript();
    }

    get sdkType() {
        return this.sdkAdapter.sdkType;
    }

    get requestHeaders() {
        return this.sdkAdapter.getRequestHeaders();
    }

    get deletedAccountToken() {
        return this.sdkAdapter.deletedAccountToken();
    }

    /**
     * 开启调试模式
     * 开启之后可以在控制台观察到jssdk调用日志
     * 同时对于企微和微信环境，还会开启其自带的调试模式
     * @param isDebug 是否开启调试模式
     */
    setIsDebug(isDebug: boolean) {
        sdkLogger.setIsDebug(isDebug);
    }

    /**
     * 调用当前客户端的分享功能。
     * resolve 返回分享结果：success 表示分享成功，cancelled 表示明确取消
     * reject 表示 SDK 调用失败。
     * 部分客户端操作不会触发回调，Promise 会保持 pending。
     */
    shareLink(options: ShareLinkOptions): Promise<ShareLinkResult | void> {
        return this.sdkAdapter.shareLink(options);
    }

    contactAdmin(id: string, corpId: string) {
        return this.sdkAdapter.contactAdmin(id, corpId);
    }

    authConfirm() {
        return this.sdkAdapter.authConfirm();
    }

    requestAuthCode(integrateCorpId: string) {
        return this.sdkAdapter.requestAuthCode(integrateCorpId);
    }

    /**
     * 业务侧注册默认实现
     * @param method 方法名
     * @param fn 默认实现的方法
     */
    registerDefaultImplementation(
        method: FunctionKeys<JsSdkBase>,
        fn: (args: any) => void | Promise<any>
    ) {
        this.sdkAdapter.defaultImplementations.set(method, fn);
    }

    previewImage(urls: string[], current: string) {
        return this.sdkAdapter.previewImage(urls, current);
    }

    getLocation(
        options: Parameters<JsSdkBase['getLocation']>[0]
    ): ReturnType<JsSdkBase['getLocation']> {
        // 原来的定位逻辑
        const locator = () => {
            return this.sdkAdapter.getLocation(options);
        };

        return new Promise((resolve, reject) => {
            /**
             * 缓存定位结果，30s内只返回缓存结果，超过则再重新请求定位（30s秒间隔参照自微信官方社区相关公告）
             * 1、当进行第一次定位时，时间戳为空，直接调用jssdk获取定位结果并缓存
             * 2、当超过30s再次进行定位，则认为缓存已超时，重新调用jssdk获取定位结果，并更新缓存
             * @see https://developers.weixin.qq.com/community/develop/doc/000aee91a98d206bc6dbe722b51801
             */
            if (
                !this.locationCache.timestamp ||
                Date.now() - this.locationCache.timestamp > 30000
            ) {
                sdkLogger.info('h5-jsapi getLocation cache is empty or expired');
                this.locationCache.timestamp = Date.now();
                this.locationCache.location = locator();
                this.locationCache.location.then(resolve, reject);
            } else {
                sdkLogger.info('h5-jsapi getLocation use cache');
                // 如果在30s内多次连续调用定位，则直接利用缓存的结果
                this.locationCache.location?.then(resolve, reject);
            }
        });
    }

    scanCode(options: Parameters<JsSdkBase['scanCode']>[0]) {
        return this.sdkAdapter.scanCode(options);
    }

    chooseImage(options: Parameters<JsSdkBase['chooseImage']>[0]) {
        return this.sdkAdapter.chooseImage(options);
    }

    notify(options: Parameters<JsSdkBase['notify']>[0]) {
        return this.sdkAdapter.notify(options);
    }

    confirm(config: Parameters<JsSdkBase['confirm']>[0]) {
        return this.sdkAdapter.confirm(config);
    }

    alert(options: Parameters<JsSdkBase['alert']>[0]) {
        return this.sdkAdapter.alert(options);
    }

    @sdkLog('h5-jsapi')
    setPageTitle(title: string) {
        if (isEmpty(title)) {
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'title is empty')
            );
        }
        document.title = title;
        return this.sdkAdapter.setPageTitle(title);
    }

    /**
     * 打开目标地址
     * 统一返回规范化的 HTTP(S) 绝对地址，相对地址基于当前页面 origin 补齐
     * 空白、无法解析或非 HTTP(S) 地址会被拒绝
     * @param url 目标地址
     */
    @sdkLog('h5-jsapi')
    openLink(url: string) {
        const targetUrl = getValidAbsoluteUrl(url);

        if (!targetUrl) {
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'url is invalid')
            );
        }
        return this.sdkAdapter.openLink(targetUrl);
    }

    @sdkLog('h5-jsapi')
    downloadLink(options: Parameters<JsSdkBase['downloadLink']>[0]) {
        if (!options?.url) {
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'url is empty')
            );
        }
        return this.sdkAdapter.downloadLink(options);
    }

    @sdkLog('h5-jsapi')
    cancelDownloadLink(options: Parameters<JsSdkBase['cancelDownloadLink']>[0]) {
        if (!options?.url) {
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'url is empty')
            );
        }
        return this.sdkAdapter.cancelDownloadLink(options);
    }

    @sdkLog('h5-jsapi')
    downloadPrintFile(downloadUrl?: string, fileType?: string, fileName?: string) {
        if (!downloadUrl) {
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'downloadUrl is empty')
            );
        }
        return this.sdkAdapter.downloadPrintFile({ url: downloadUrl, fileType, fileName });
    }

    navigateTo(url: string) {
        return this.sdkAdapter.navigateTo(url);
    }

    screenRotate() {
        return this.sdkAdapter.screenRotate();
    }

    resetScreenRotate() {
        return this.sdkAdapter.resetScreenRotate();
    }

    screenRotateBeforeFullscreen(fullscreen: boolean) {
        return this.sdkAdapter.screenRotateBeforeFullscreen(fullscreen);
    }

    closeWindow() {
        return this.sdkAdapter.closeWindow();
    }

    openMap(params: Parameters<JsSdkBase['openMap']>[0]) {
        return this.sdkAdapter.openMap(params);
    }

    applyAppScope() {
        return this.sdkAdapter.applyAppScope();
    }

    disableBounce() {
        return this.sdkAdapter.disableBounce();
    }

    switchCorp(tenantId?: string) {
        return this.sdkAdapter.switchCorp(tenantId);
    }

    requestCameraPermission() {
        return this.sdkAdapter.requestCameraPermission();
    }

    signout() {
        return this.sdkAdapter.signout();
    }

    onCameraPermissionChanged(callback: (res: string) => void): () => void {
        return this.sdkAdapter.onCameraPermissionChanged(callback);
    }

    /** 设置当前网页的导航栏业务按钮。 */
    setHeaderActions(params: Parameters<JsSdkBase['setHeaderActions']>[0]): Promise<void> {
        return this.sdkAdapter.setHeaderActions(params);
    }

    /** 清除当前网页的业务按钮配置。 */
    clearHeaderActions(params?: Parameters<JsSdkBase['clearHeaderActions']>[0]): Promise<void> {
        return this.sdkAdapter.clearHeaderActions(params);
    }

    /** 同步返回当前网页按钮事件的取消函数。 */
    onHeaderActionClick(callback: (event: HeaderActionClickEvent) => void): () => void {
        return this.sdkAdapter.onHeaderActionClick(callback);
    }

    /** 请求添加当前业务的桌面入口。 */
    addToDesktop(
        params: Parameters<JsSdkBase['addToDesktop']>[0]
    ): ReturnType<JsSdkBase['addToDesktop']> {
        return this.sdkAdapter.addToDesktop(params);
    }

    hasFunction(functionName: SDKFunction): Promise<boolean> {
        return this.sdkAdapter.hasFunction(functionName);
    }

    goToAboutApp(): Promise<void> {
        return this.sdkAdapter.goToAboutApp();
    }

    goToCreateCorp(): Promise<void> {
        return this.sdkAdapter.goToCreateCorp();
    }
}
